import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { formatDate } from '../../../utils/DateUtils';
import { getProformaStatusColor, getProformaStatusText } from '../../../utils/ProformaStatusUtils';
import dayjs from 'dayjs';
import { formatMoney } from '../../../utils/currency';
import { describeOrderChange } from '../../../utils/orderChangeDiff';

const ViewProformaModal = ({ open, onClose, proforma }) => {
  const { t } = useTranslation();

  if (!proforma) return null;

  const formatCurrency = (amount) => {
    return formatMoney(amount);
  };

  // Una vez aceptada ya no importa el stock actual: se muestra lo pedido contra lo que quedó confirmado.
  const isAccepted = proforma.status === 'ACCEPTED';

  // Calcular totales generales
  const totalAmount = proforma.items?.reduce((total, item) => total + (item.totalPrice || 0), 0) || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getIcon('eye')}
        {t('proforma_details')}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Información General */}
          <Typography variant="h6" gutterBottom>
            {t('general_information')}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('proforma_number')}:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {proforma.id}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('branch')}:
                </Typography>
                <Typography variant="body1">
                  {proforma.branchName || `Sucursal ${proforma.branchId}`}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('status')}:
                </Typography>
                <Chip
                  label={getProformaStatusText(proforma.status, t)}
                  color={getProformaStatusColor(proforma.status)}
                  size="small"
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('client')}:
                </Typography>
                <Typography variant="body1">
                  {proforma.nombreFantasia || proforma.razonSocial || proforma.clientName || t('unassigned')}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('validity_days')}:
                </Typography>
                <Typography variant="body1">
                  {proforma.validityDays} {t('days')}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('issue_date')}:
                </Typography>
                <Typography variant="body1">
                  {formatDate(dayjs(proforma.issueDate))}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('expiration_date')}:
                </Typography>
                <Typography variant="body1">
                  {formatDate(dayjs(proforma.issueDate).add(proforma.validityDays, 'days'))}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Items de la Proforma */}
          <Typography variant="h6" gutterBottom>
            {t('proforma_items')}
          </Typography>
          
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('product')}</TableCell>
                  <TableCell align="center">Solicitado</TableCell>
                  {isAccepted ? (
                    <TableCell align="center">Aceptado</TableCell>
                  ) : (
                    <TableCell align="center">Stock actual</TableCell>
                  )}
                  <TableCell align="center">{t('unit_price')}</TableCell>
                  <TableCell align="right">{t('total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {proforma.items?.map((item, index) => {
                  const finalUnitPrice = item.quantity > 0 ? item.totalPrice / item.quantity : item.unitPrice;
                  
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {item.productName}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{item.requestedQuantity ?? item.quantity}</TableCell>
                      {isAccepted ? (
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            color={(item.requestedQuantity ?? item.quantity) !== item.quantity ? 'primary.main' : 'text.primary'}
                            sx={{ fontWeight: (item.requestedQuantity ?? item.quantity) !== item.quantity ? 700 : 400 }}
                          >
                            {item.quantity}
                          </Typography>
                        </TableCell>
                      ) : (
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            color={item.currentStock != null && item.currentStock < (item.requestedQuantity ?? item.quantity) ? 'error.main' : 'text.primary'}
                            sx={{ fontWeight: item.currentStock != null && item.currentStock < (item.requestedQuantity ?? item.quantity) ? 700 : 400 }}
                          >
                            {item.currentStock != null ? item.currentStock : '-'}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="center">
                        {formatCurrency(finalUnitPrice)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(item.totalPrice)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totales */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Box sx={{ textAlign: 'right', minWidth: 300 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {t('total')}:
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(totalAmount)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Modificaciones respecto de lo que pidió el cliente (quitados, agregados, cantidades, precios) */}
          {proforma.modifications?.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Modificaciones
              </Typography>
              {proforma.modifications.map((modification) => (
                <Box
                  key={modification.id}
                  sx={{ mb: 2, p: 2, borderRadius: 1, border: '1px solid', borderColor: 'warning.light', backgroundColor: 'rgba(255, 193, 7, 0.08)' }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(dayjs(modification.createdAt))}
                  </Typography>
                  {modification.note && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: 1 }}>
                      {modification.note}
                    </Typography>
                  )}
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {modification.changes.map((change) => (
                      <li key={`${change.type}-${change.productId}`}>
                        <Typography variant="body2">
                          <strong>{change.productName}</strong>: {describeOrderChange(change)}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Total: {formatCurrency(modification.previousTotal)} → <strong>{formatCurrency(modification.newTotal)}</strong>
                  </Typography>
                </Box>
              ))}
            </>
          )}

          {/* Notas */}
          {proforma.notes && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                {t('notes')}
              </Typography>
              <Typography variant="body2" sx={{ 
                backgroundColor: 'grey.50', 
                padding: 2, 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                {proforma.notes}
              </Typography>
            </>
          )}

        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewProformaModal;
